import { PlanType } from '@labrav/react-components'
import type { FlyoutMicrosoftProductSections } from '../../types/addEditProduct'

export type { FlyoutMicrosoftProductSections } from '../../types/addEditProduct'

export enum ProductsListingActionTypes {
  SET_PRODUCTS = 'SET_PRODUCTS',
  SET_PRODUCTS_V2 = 'SET_PRODUCTS_V2',
  SET_PRODUCTS_WITH_LISTING_DATA = 'SET_PRODUCTS_WITH_LISTING_DATA',
  CLEAR_PRODUCTS = 'CLEAR_PRODUCTS',
  UPDATE_CLOUD_FREE_TRIAL_OFFER_ID = 'UPDATE_CLOUD_FREE_TRIAL_OFFER_ID',
  SET_SELECTED_PRODUCT = 'SET_SELECTED_PRODUCT',
  SET_FREE_TIER_DATA = 'SET_FREE_TIER_DATA',
  SET_PRODUCT_DETAIL = 'SET_PRODUCT_DETAIL',
  CLEAR_PRODUCT_DETAIL = 'CLEAR_PRODUCT_DETAIL',
}

export enum FreeTrialStatus {
  FREE_TRIAL_CREATION_IN_PROGRESS = 'FREE_TRIAL_CREATION_IN_PROGRESS',
  FREE_TRIAL_CREATED = 'FREE_TRIAL_CREATED',
  FREE_TRIAL_DEACTIVATION_IN_PROGRESS = 'FREE_TRIAL_DEACTIVATION_IN_PROGRESS',
  FREE_TRIAL_DEACTIVATED = 'FREE_TRIAL_DEACTIVATED',
}

export enum FlyoutFunction {
  'REVENUE' = 'revenue',
  'PRIVATE_OFFERS' = 'privateOffers',
  'RESALE_AUTHORIZATIONS' = 'resaleAuthorizations',
  'SUBSCRIPTION' = 'subscription',
}

export enum EulaType {
  STANDARD = 'STANDARD',
  ENTERPRISE = 'ENTERPRISE',
  CUSTOM = 'CUSTOM',
  PUBLIC = 'PUBLIC',
}

export enum PlanTypeGCP {
  USAGE = 'USAGE',
  FLAT_FEE = 'FLAT_FEE',
  FLAT_FEE_USAGE = 'FLAT_FEE_USAGE',
}

export enum AzurePriceInputOption {
  PERMARKET = 'perMarket',
  USD = 'usd',
}

export enum ScheduleType {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
  CUSTOM = 'CUSTOM',
}

export interface PriceTypes {
  price1m: number | null
  price12m: number | null
  price24m: number | null
  price36m: number | null
  additionalUsagePrice: number | null
  subscriptionPrice: number | null
  price?: number
}

export type Price = number | PriceTypes

export interface BillingTerm {
  type: string
  value: number
}

export interface PaymentOption {
  type: string
  value: number
}

export interface Installments {
  paymentDate: string
  amount?: number | null
  currency: string
  flatFee?: number
  otherAmount?: number | null
  chargeDate?: string
  pricePerPaymentInUsd?: number | null
  isImmediateInstallment?: boolean
}

export interface AzureInstallments {
  schedule?: Installments[]
  tcv?: number
  frequency?: ScheduleType
}

export interface AzureRecurrentPriceField {
  billingTerm: BillingTerm
  paymentOption: PaymentOption
  pricePerPaymentInUsd?: number
  installments?: AzureInstallments
}

export interface IncludedQuantity {
  billingTerm: BillingTerm
  isInfinite: boolean
  quantity: number
}

export interface Features {
  label: string
  status: string
  value: string
}

export interface links {
  self: string
  nextPage?: string
  previousPage?: string
}

export type CloudMarketplace = 'AWS' | 'GCP' | 'AZURE' | 'REDHAT'

export enum FlyOutAWSProductListingType {
  'SAAS' = 'SAAS',
  'PROFESSIONAL_SERVICES' = 'PROFESSIONAL_SERVICES',
  'CONTAINER' = 'CONTAINER',
  'AGENTIC_AI' = 'AGENTIC_AI',
}

export interface FreeTrial {
  freeTrialStatus?: FreeTrialStatus
  freeTrialEnabled?: boolean
  eulaType?: EulaType
  eulaFiles?: string[]
  freeTrial?: boolean
  freeTrialEnabledAt?: string
  labraFreeTrialId?: string
  freeTrialOfferId?: string
  freeTrialExpiresInDays?: number
  cloudFreeTrialOfferId?: string
  cloudFreeTrialOfferName?: string
  enabled?: boolean
  quantity?: number
}

export enum ProductStatus {
  PRODUCT_STATUS_PUBLIC = 'Public',
  PRODUCT_STATUS_IN_PROGRESS = 'In-progress',
  PRODUCT_STATUS_LABRA_TESTING = 'Labra-testing',
  PRODUCT_STATUS_LIMITED = 'Limited',
  PRODUCT_STATUS_DRAFT = 'Draft',
  PRODUCT_STATUS_CREATION_FAILED = 'Failed',
  PRODUCT_STATUS_AWS_TESTING = 'AWS-testing',
  PRODUCT_STATUS_RESTRICTED = 'Restricted',
  PRODUCT_STATUS_REJECTED = 'Rejected',
  PRODUCT_STATUS_MIGRATION_IN_QUEUE = 'MIGRATION_IN_QUEUE',
  MIGRATION_FAILED = 'MIGRATION_FAILED',
  MIGRATION_IN_PROGRESS = 'MIGRATION_IN_PROGRESS',
}

export enum ContainerPricingModel {
  CONTRACT = 'CONTRACT',
  HOURLY = 'HOURLY',
  USAGE = 'USAGE',
  FREE = 'FREE',
  BYOL = 'BYOL',
  MONTHLY = 'MONTHLY',
}

export const containerProductHourlyAndUsage = [
  ContainerPricingModel.HOURLY,
  ContainerPricingModel.USAGE,
]

export const resalePricesKey: string[] = [
  'price1m',
  'price12m',
  'price24m',
  'price36m',
  'additionalUsagePrice',
  'subscriptionPrice',
]

export interface CommonDimensionField {
  dimensionId?: string
  id?: string
  label: string
  name: string
  valueDataType?: string
  price?: Price
  createdAt?: string
  currency: string
}

export interface ProductDimension extends CommonDimensionField {
  type?: string
  labelDescription: string
  isAdditionalUsageAllowed?: boolean
  additionalUsageDescription?: string
  prices?: Price
  pricePerName: string
  awsProductId: string
  freeTrial?: FreeTrial
  additionalUsagePrice?: number
}

export interface AzurePlanDimensionMeter {
  cloudDimensionId: string
  id?: string
  azureDimensionId: string
  label: string
  name: string
  valueDataType?: string
  createdAt?: string
  currency: string
  includedQuantities: IncludedQuantity[]
  pricePerPaymentInUsd: number
  unitOfMeasure: string
  meterType?: string
  meteringDimensionName?: string
  metering_dimension_name?: string
  price_per_payment_in_usd?: number
  unit_of_measure?: string
  meter_type?: string
  cloud_dimension_id?: string
  included_quantities?: IncludedQuantity[]
}

export interface AzurePlanDimension {
  meters: AzurePlanDimensionMeter[]
  priceInputOption: AzurePriceInputOption
}

export interface GcpPlanPrice {
  price1m: number | null
  price12m: number | null
  price24m: number | null
  price36m: number | null
}

export interface ProductPlan {
  planId: string
  azurePlanId?: string
  cloudPlanId?: string
  displayName: string
  description: string
  minQuantity?: number
  maxQuantity?: number
  planType?: PlanTypeGCP
  prices?: GcpPlanPrice
  dimensions: AzurePlanDimension | CommonDimensionField[]
  pricing?: AzureRecurrentPriceField[]
  features?: Features[]
  isPrivate?: boolean
  isStopSell?: boolean
  hasFreeTrials?: boolean
}

export interface Listingdata {
  searchKeywords?: string
  searchKeyword?: string
}

export interface Product {
  id?: string
  listingData?: Listingdata | FlyoutMicrosoftProductSections
  productId: string
  productName: string
  cloudMarketplaceProductId: string
  cloudMarketplaceProductCode: string
  numberOfContracts: number
  eulaUrl?: string
  numberOfActiveContracts?: number
  cloudMarketplace: CloudMarketplace
  status: ProductStatus
  marketplaceUrl: string
  productUrl?: string
  productDimensions?: ProductDimension[]
  sellerId: string
  createdAt: string
  productType?: PlanType
  updatedAt: string
  freeTrial?: FreeTrial
  links: links
  emailCustomMessage?: string
  plans?: ProductPlan[]
  containerPricingModel?: ContainerPricingModel
  awsProductId?: string
  isAgenticProduct?: boolean
  isConcurrentEnabled?: boolean
  concurrentEnabledBuyers?: boolean
}

export const notAllowedProductSelection = [
  ProductStatus.PRODUCT_STATUS_DRAFT,
  ProductStatus.PRODUCT_STATUS_IN_PROGRESS,
  ProductStatus.PRODUCT_STATUS_REJECTED,
  ProductStatus.PRODUCT_STATUS_RESTRICTED,
]

export const allowedToDeleteProductSelection = [
  ProductStatus.PRODUCT_STATUS_DRAFT,
  ProductStatus.PRODUCT_STATUS_CREATION_FAILED,
]

export const allowedToPublishProductSelection = [
  ProductStatus.PRODUCT_STATUS_REJECTED,
  ProductStatus.PRODUCT_STATUS_RESTRICTED,
]

export const allowedToUnpublishProductSelection = [
  ProductStatus.PRODUCT_STATUS_PUBLIC,
]

export interface FreeTierInfo {
  additionalAllowedProducts?: number
}

export interface ProductsV2State {
  rows: Product[]
  pageNumber: number
  pageSize: number
  totalCount: number
  hasMore: boolean
}

export interface ProductsListingState {
  selectedProduct?: string
  productsV2: ProductsV2State
  products: {
    rows: Product[]
    pageNumber?: number
    pageSize?: number
    totalCount?: number
    hasNextPage?: boolean
  }
  productWithListingData?: {
    rows: Product[]
  }
  freeTierInfo?: FreeTierInfo
  productDetail?: Product
}

export type AlertMessages = {
  success?: string
  error?: string
}

export type DeactivateFreeTrialParams = {
  labraFreeTrialId: string
  partnerId: string
  productId: string
  cloudMarketplace: string
}

/** @deprecated Use DeactivateFreeTrialParams */
export type deactivateFreeTrialParams = DeactivateFreeTrialParams

export type UpdateProductListingDataArgs = {
  partnerId: string
  productId: string
  data: Record<string, unknown>
  alertMessages?: AlertMessages
  handleToggleChecked?: () => void
}

export type EnableConcurrentForBuyerArgs = {
  partnerId: string
  productId: string
  accountIds?: string[]
  hardOpt?: boolean
  callBack?: (data: { success: boolean }) => void
}

export type ProductListingMutationArgs = {
  partnerId: string
  productId: string
  alertMessages?: AlertMessages
  handleToggleChecked?: () => void
}

export type GetProductInfoDetailArgs = {
  accountId: string
  cloudMarketplace: CloudMarketplace
  productId: string
}
